import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { getScoreValidationSchema } from "@modules/scoreboard/scoreTypes"
import { convertScoreToInputValue } from "@modules/scoreboard/scoreUtils"
import { ScoreFormikType } from "@modules/sessionBlock/exercises/exerciseFormikTypes"
import { Exercise } from "@modules/sessionBlock/exercises/exerciseTypes"
import { convertScoreFromDraft } from "@modules/sessionBlock/exercises/exerciseUtils"
import { ParentSessionOrigin } from "@modules/sessionBlock/exercises/sessionTypes"
import { useToast } from "@modules/ui/components/huToast"
import { useUnit } from "@modules/utils/hooks/useUnit"
import { useFormik } from "formik"
import { useState } from "react"

export const useScoreFormik = (
  parentSessionOrigin: ParentSessionOrigin,
  exercise: Exercise,
  successCallback: () => void,
) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const { sessionService } = useServicesContext()
  const toast = useToast()
  const unit = useUnit()

  const exerciseScoreFormik = useFormik<ScoreFormikType>({
    initialValues: {
      value: convertScoreToInputValue(exercise.score?.value, exercise.scoreType, unit),
      difficulty: exercise.score?.difficulty ?? "RX",
      note: exercise.score?.note ?? "",
      noteAndMediaIsVisible: exercise.score?.noteAndMediaIsVisible ?? true,
      medias: exercise.score?.medias?.map((media) => ({ ...media, awsS3Key: media.key ?? undefined })) ?? [],
    },
    validationSchema: getScoreValidationSchema(exercise.scoreType),
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true)

        await sessionService.addScoreToExercise(
          parentSessionOrigin,
          exercise,
          convertScoreFromDraft(values, exercise.scoreType, unit),
        )

        toast.show({
          severity: "success",
          summary: word("score.scoreUpdated.toastSuccess"),
        })
        setIsSubmitting(false)
        setTimeout(() => {
          successCallback()
        }, 500)
      } catch (e: any) {
        setIsSubmitting(false)
        console.error("Error on update a score", e)
        toast.show({ severity: "error", summary: "Error", detail: e.message })
      }
    },
  })

  return { exerciseScoreFormik, isSubmitting }
}
